import React, { useEffect, useMemo } from "react";
import {
  DataGrid,
  createTableColumn,
  TableCellLayout,
  DataGridBody,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridCell,
  Field,
} from "@fluentui/react-components";
import { useTaskPane } from "../../provider/TaskPaneProvider";
import { dateFormat, datetoTimestampFormat } from "../../../utils/formats";

const RecentList = ({ updateState }) => {
  const {
    taskPaneState,
    policiesDetailsForInsured,
    filterTypeByCriteriaType,
    setSelectedGridItem,
    selectedGridItem,
    selectedItemDetailFromGrid,
  } = useTaskPane();
  console.log('selectedGridItem', selectedGridItem);


// Reusable compare function for string fields
function compareStrings(a, b, key) {
  const A = a[key] ?? "";
  const B = b[key] ?? "";
  return A.localeCompare(B);
}

// Reusable compare function for dates (assuming datetoTimestampFormat is defined)
function compareDates(a, b, key) {
  return datetoTimestampFormat(a[key]) - datetoTimestampFormat(b[key]);
}

// Reusable compare function for numbers
function compareNumbers(a, b, key) {
  return (+a[key] || 0) - (+b[key] || 0);
}

function compareCurrency(a, b, key) {
  const A = parseFloat((a[key] ?? "0").replace(/[$,]/g, ""));
  const B = parseFloat((b[key] ?? "0").replace(/[$,]/g, ""));
  return A - B;
}

// Table column definitions
const columns = [
  createTableColumn({
    columnId: "sub",
    compare: (a, b) => compareNumbers(a, b, "sub"),
    renderHeaderCell: () => "Sub #",
    renderCell: (item) => <TableCellLayout>{item.sub}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "policy",
    compare: (a, b) => compareStrings(a, b, "policy"),
    renderHeaderCell: () => "Policy #",
    renderCell: (item) => <TableCellLayout>{item.policy}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "effectiveDate",
    compare: (a, b) => compareDates(a, b, "effectiveDate"),
    renderHeaderCell: () => "Effective Date",
    renderCell: (item) => <TableCellLayout>{dateFormat(item.effectiveDate)}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "subStatus",
    compare: (a, b) => compareStrings(a, b, "subStatus"),
    renderHeaderCell: () => "Status",
    renderCell: (item) => <TableCellLayout>{item.subStatus}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "lob",
    compare: (a, b) => compareStrings(a, b, "lob"),
    renderHeaderCell: () => "LOB",
    renderCell: (item) => <TableCellLayout>{item.lob}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "provider",
    compare: (a, b) => compareStrings(a, b, "provider"),
    renderHeaderCell: () => "Provider",
    renderCell: (item) => <TableCellLayout>{item.provider}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "underwriter",
    compare: (a, b) => compareStrings(a, b, "underwriter"),
    renderHeaderCell: () => "Underwriter",
    renderCell: (item) => <TableCellLayout>{item.underwriter}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "currentPremium",
    compare: (a, b) => compareCurrency(a, b, "currentPremium"),
    renderHeaderCell: () => "Premium",
    renderCell: (item) => <TableCellLayout>{item.currentPremium}</TableCellLayout>,
  }),
  createTableColumn({
    columnId: "subLobs",
    compare: (a, b) => compareStrings(a, b, "subLobs"),
    renderHeaderCell: () => "SubLobs",
    renderCell: (item) => <TableCellLayout>{item.subLobs}</TableCellLayout>,
  }),
];

const columns1 = [
    createTableColumn({
      columnId: "number",
      compare: (a, b) => compareNumbers(a, b, "number"),
      renderHeaderCell: () => "Number",
      renderCell: (item) => {
        return <TableCellLayout>{item.number}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "name",
      compare: (a, b) => compareStrings(a, b, "name"),
      renderHeaderCell: () => "Name",
      renderCell: (item) => {
        return <TableCellLayout>{item.name}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "tradingAsName",
      compare: (a, b) => compareStrings(a, b, "tradingAsName"),
      renderHeaderCell: () => "Trading As Name",
      renderCell: (item) => {
        return <TableCellLayout>{item.tradingAsName}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "city",
      compare: (a, b) => compareStrings(a, b, "city"),
      renderHeaderCell: () => "City",
      renderCell: (item) => {
        return <TableCellLayout>{item.city}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "state",
      compare: (a, b) => compareStrings(a, b, "state"),
      renderHeaderCell: () => "State",
      renderCell: (item) => {
        return <TableCellLayout>{item.state}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "zip",
      compare: (a, b) => compareStrings(a, b, "zip"),
      renderHeaderCell: () => "Zip",
      renderCell: (item) => {
        return <TableCellLayout>{item.zip}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "phone",
      compare: (a, b) => compareStrings(a, b, "phone"),
      renderHeaderCell: () => "Phone #",
      renderCell: (item) => {
        return <TableCellLayout>{item.phone}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "active",
      compare: (a, b) => compareStrings(a, b, "active"),
      renderHeaderCell: () => "Active",
      renderCell: (item) => {
        return <TableCellLayout>{item.active}</TableCellLayout>;
      },
    }),
    createTableColumn({
      columnId: "yourAgy",
      compare: (a, b) => compareStrings(a, b, "yourAgy"),
      renderHeaderCell: () => "Your Agy",
      renderCell: (item) => {
        return <TableCellLayout>{item.yourAgy}</TableCellLayout>;
      },
    }),
  ];
  
  const columnTypeBasedOnFilter = useMemo(
    () =>
      filterTypeByCriteriaType === "Type Insured/Policy"
        ? columns
        : filterTypeByCriteriaType === "Type Producer"
          ? columns1
          : columns,
    [filterTypeByCriteriaType, columns, columns1]
  );
 const columnSizingOptions = {
  sub: {
    minWidth: 70,
    defaultWidth: 70,
  },
  policyID: {
    defaultWidth: 70,
    minWidth: 70,
    idealWidth: 70,
  },
    policy: {
    defaultWidth: 155,
    minWidth: 100,
    idealWidth: 155,
  },
  effectiveDate: {
    minWidth: 85,
    defaultWidth: 85,
  },
    subStatus: {
    minWidth: 75,
    defaultWidth: 75,
  },
    currentPremium: {
    minWidth: 75,
    defaultWidth: 75,
  },
    number: {
    minWidth: 75,
    defaultWidth: 75,
  },
    state: {
    minWidth: 75,
    defaultWidth: 75,
  },
    zip: {
    minWidth: 75,
    defaultWidth: 75,
  },
    active: {
    minWidth: 75,
    defaultWidth: 75,
  },
    yourAgy: {
    minWidth: 75,
    defaultWidth: 75,
  },
    phone: {
    minWidth: 90,
    defaultWidth: 90,
  },
};
useEffect(() => {
if(policiesDetailsForInsured?.length > 4) {
setTimeout(() => {
        const element = document.getElementById(`recent-${selectedItemDetailFromGrid?.policyID}`);
      console.log('element-check id', `recent-${selectedItemDetailFromGrid?.policyID}`);
      console.log('element', element);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
}, 100);

}
setSortState({});
}, [policiesDetailsForInsured])

  const [sortState, setSortState] = React.useState({});
  const onSortChange = (e, nextSortState) => {
    setSortState(nextSortState);
  };

  return (
      <div className="mb-0">
       <h2 className="h2-text text-left font-semibold text-gray-700 mb-1">Recent</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 sm:gap-2 lg:grid-cols-4 lg:gap-2 h-auto sm:h-36">
        {/* Insured Dropdown List */}
          {filterTypeByCriteriaType === "Type Insured/Policy" && (
              <div className="sm:mt-0 md:mt-0 lg:mt-0 p-0 card-layout overflow-x-auto w-full recent-bg">
            <Field
              validationState={
                taskPaneState?.insuredForRecentPoliciesResults?.length == 0 ? "warning" : "none"
              }
              validationMessage={
                taskPaneState?.insuredForRecentPoliciesResults?.length == 0 ? "No data found!" : ""
              }
            >
              <select
                size="6"
                value={taskPaneState?.insuredId || ""}
                onChange={(e) => updateState("insuredId", e.target.value)}
                style={{ height: taskPaneState?.insuredForRecentPoliciesResults?.length == 0? "6.5rem" : "8rem" }}
                className="select-recent rounded w-full text-gray-700 text-sm focus:outline-none recent-list recent-bg"
              >
                {/* <option value={""}>Select</option> */}
                {taskPaneState?.insuredForRecentPoliciesResults?.map((ifrp) => (
                  <option key={ifrp.insuredID} value={ifrp.insuredID} className="recent-opt text-gray-700">
                    {ifrp.legalName || ifrp.insureds}
                  </option>
                ))}
              </select>
            </Field>
        </div>
          )}
        {/* Data Table */}
        <div className={`${filterTypeByCriteriaType === "Type Insured/Policy"? "col-span-3" : "col-span-4"} sm:mt-0 md:mt-0 lg:mt-0 p-0 card-layout overflow-x-auto`} style={{ marginLeft: '0' }}>
          <DataGrid
          resizableColumnsOptions={{
          autoFitColumns: false,
        }}
            sortable={policiesDetailsForInsured?.length}
            sortState={sortState}
            onSortChange={onSortChange}
            columnSizingOptions = {columnSizingOptions}
            resizableColumns
            selectionMode={"single"}
            items={policiesDetailsForInsured}
            columns={columnTypeBasedOnFilter}
            selectedItems={selectedGridItem}
            onSelectionChange={(_, data) => setSelectedGridItem(data.selectedItems)}
            getRowId={(item) => item.policyID}
          >
                      <DataGridHeader as="thead" className="sticky-header" >
                          <DataGridRow style={{ color: "#fff", background: "#102246", borderRadius:'4px', height: "26px" }}>
                              {({ renderHeaderCell }) => (
                                  <DataGridHeaderCell className="cell-styl  sort-cell-styl">
                              {renderHeaderCell()}
                          </DataGridHeaderCell>
    )}
                      </DataGridRow>
                  </DataGridHeader>


            <DataGridBody>
              {({ item, rowId }) => (
                <DataGridRow key={rowId} id={`recent-${rowId}`} style={{ height: '26px' }} selectionCell={{ radioIndicator: { "aria-label": "Select row" } }}>
                  {({ renderCell }) => <DataGridCell className="cell-styl">{renderCell(item)}</DataGridCell>}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
          {/* {policiesDetailsForInsured.length == 0 && <div>No Result Found</div>} */}
              </div>

      </div>
    </div>
  );
};

export default RecentList;
